const SESSIONS_RECONCILE_INTERVAL_MS = 2000;
const SESSIONS_RECONCILE_MAX_ATTEMPTS = 10;

interface ReconcileState {
  knownDeadSessionIds: Set<string>;
  reconcileIntervalId: NodeJS.Timeout | null;
  reconcileAttempts: number;
}

const reconcileStateMap = new WeakMap<QueryClient, ReconcileState>();

function getReconcileState(queryClient: QueryClient): ReconcileState {
  let state = reconcileStateMap.get(queryClient);

  if (!state) {
    state = {
      knownDeadSessionIds: new Set<string>(),
      reconcileIntervalId: null,
      reconcileAttempts: 0,
    };
    reconcileStateMap.set(queryClient, state);
  }

  return state;
}

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  UseQueryOptions,
  UseMutationResult,
  QueryObserverBaseResult,
  QueryClient,
} from '@tanstack/react-query';
import { IdpSessionsService } from '@/api/user-portal-service';
import { queryKeys } from '@/common/queryClient';
import type { TransformedSession } from '@/utils/session-utils';
import { transformSession } from '@/utils/session-utils';

interface UseIdpSessionsResult {
  sessions: TransformedSession[];
  total: number;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: QueryObserverBaseResult['refetch'];
}

function startReconcileLoop(queryClient: QueryClient) {
  const state = getReconcileState(queryClient);

  if (state.reconcileIntervalId !== null) {
    clearInterval(state.reconcileIntervalId);
  }

  state.reconcileAttempts = 0;

  state.reconcileIntervalId = setInterval(async () => {
    state.reconcileAttempts++;

    await queryClient.refetchQueries({
      queryKey: queryKeys.sessions.active(),
    });

    if (
      state.knownDeadSessionIds.size === 0 ||
      state.reconcileAttempts >= SESSIONS_RECONCILE_MAX_ATTEMPTS
    ) {
      if (state.reconcileIntervalId !== null) {
        clearInterval(state.reconcileIntervalId);
        state.reconcileIntervalId = null;
      }
    }
  }, SESSIONS_RECONCILE_INTERVAL_MS);
}

export function useIdpSessions(
  options?: Omit<
    UseQueryOptions<TransformedSession[], Error>,
    'queryKey' | 'queryFn'
  >
): UseIdpSessionsResult {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.sessions.active(),
    queryFn: async () => {
      const response = await IdpSessionsService.getMyActiveSessions(0, 100);
      const state = getReconcileState(queryClient);

      const serverSessionIds = new Set(
        (response.items ?? []).map(item => item.idp_session_id)
      );

      for (const deadId of state.knownDeadSessionIds) {
        if (!serverSessionIds.has(deadId)) {
          state.knownDeadSessionIds.delete(deadId);
        }
      }

      return (response.items ?? []).map(transformSession);
    },
    select: (sessions: TransformedSession[]) => {
      const state = getReconcileState(queryClient);

      return sessions.filter(
        session => !state.knownDeadSessionIds.has(session.id)
      );
    },
    ...options,
  });

  const sessions = query.data ?? [];

  return {
    sessions,
    total: sessions.length,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useSignOutSession(): UseMutationResult<
  void,
  Error,
  string,
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId: string) => {
      await IdpSessionsService.signOutSession(sessionId);
    },
    onSuccess: (_data, sessionId) => {
      const state = getReconcileState(queryClient);

      state.knownDeadSessionIds.add(sessionId);

      queryClient.setQueryData<TransformedSession[]>(
        queryKeys.sessions.active(),
        old => {
          if (!old) {
            return old;
          }

          return old.filter(
            session => !state.knownDeadSessionIds.has(session.id)
          );
        }
      );

      startReconcileLoop(queryClient);
    },
  });
}

export function useSignOutAllOtherSessions(): UseMutationResult<
  void,
  Error,
  void,
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await IdpSessionsService.signoutCurrentUserFromAllOtherSessions();
    },
    onSuccess: () => {
      const state = getReconcileState(queryClient);
      const currentSessions = queryClient.getQueryData<TransformedSession[]>(
        queryKeys.sessions.active()
      );

      if (currentSessions) {
        const revokedSessions = currentSessions.filter(
          session => !session.isCurrentSession && session.deviceId === null
        );

        revokedSessions.forEach(session => {
          state.knownDeadSessionIds.add(session.id);
        });
      }

      queryClient.setQueryData<TransformedSession[]>(
        queryKeys.sessions.active(),
        old => {
          if (!old) {
            return old;
          }

          return old.filter(
            session =>
              session.isCurrentSession ||
              session.deviceId !== null ||
              !state.knownDeadSessionIds.has(session.id)
          );
        }
      );

      startReconcileLoop(queryClient);
    },
  });
}
